import { createPortal } from 'react-dom';
import type { MutableRefObject } from 'react';
import EndTimeForeverPicker from './EndTimeForeverPicker';
import WeekGroup from './WeeKGroup';
import type { TimeRangeItem } from './TimeGroup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'lib/dayjs';
import produce from 'immer';
import type { DayOfWeeks } from './WeeKGroup';
import { useLatest } from 'ahooks';

type WeekTypeValue = {
  dtRange:
    | {
        startAt: string | undefined;
        endAt: string | undefined;
      }
    | undefined;
  weekRepeat:
    | {
        dayOfWeeks: DayOfWeeks;
        timeRanges: Pick<TimeRangeItem, 'startAt' | 'endAt'>[];
      }[]
    | undefined;
};

type TypeWeekPickerImplProps = {
  hookRef: MutableRefObject<null>;
  value?: WeekTypeValue;
  onChange?: (value: WeekTypeValue) => void;
};

function defaultValue(): WeekTypeValue {
  return {
    dtRange: {
      startAt: undefined,
      endAt: undefined,
    },
    weekRepeat: [],
  };
}

function TypeWeekPickerImpl(props: TypeWeekPickerImplProps) {
  const { hookRef, value, onChange } = props;
  const onChangeRef = useLatest(onChange);
  const [innerValue, setInnerValue] = useState(value || defaultValue());

  const handleRangeChange = useCallback((value) => {
    setInnerValue(
      produce((draft) => {
        if (!value[0]) {
          draft.dtRange = undefined;
        } else {
          draft.dtRange = {
            startAt: value[0] || '',
            endAt: value[1] || '',
          };
        }
      }),
    );
  }, []);

  const dtRangeDefaultValue = useMemo(() => {
    const tryValue = innerValue?.dtRange;
    if (tryValue === undefined) {
      return undefined;
    } else {
      try {
        const range = [];
        if (tryValue.startAt) {
          range.push(dayjs(tryValue.startAt, 'YYYY-MM-DD'));
          if (tryValue.endAt) {
            range.push(dayjs(tryValue.endAt, 'YYYY-MM-DD'));
          }
        } else {
          return null;
        }
        return range as [Dayjs, Dayjs];
      } catch (error) {
        return null;
      }
    }
  }, [innerValue]);

  const handleWeekValueChange = useCallback((value) => {
    setInnerValue(
      produce((draft) => {
        draft.weekRepeat = value;
      }),
    );
  }, []);

  useEffect(() => {
    onChangeRef.current?.(innerValue);
  }, [onChangeRef, innerValue]);

  if (!hookRef.current) return null;
  return (
    <>
      <EndTimeForeverPicker
        canNotSelectEnd
        noForever
        value={dtRangeDefaultValue}
        onChange={handleRangeChange}
      />
      {createPortal(
        <WeekGroup value={innerValue.weekRepeat} onChange={handleWeekValueChange} />,
        hookRef.current!,
      )}
    </>
  );
}

export default TypeWeekPickerImpl;
